import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, Users, TrendingUp, Clock, CheckCircle, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { getStorageUrl } from "@/lib/storage";

interface ConnectionWithProfiles {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: string;
  created_at: string;
  accepted_at: string | null;
  updated_at: string;
  requester: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  };
  recipient: {
    id: string;
    full_name: string;
    avatar_url: string | null;
    role: string;
  };
}

interface ConnectionStats {
  total_connections: number;
  pending_requests: number;
  accepted_today: number;
  requests_today: number;
}

export function ConnectionsManager() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "pending" | "accepted" | "declined">("all");
  const [connectionToDelete, setConnectionToDelete] = useState<ConnectionWithProfiles | null>(null);

  // Fetch all connections with profiles
  const { data: connections, isLoading } = useQuery({
    queryKey: ["admin_connections", activeTab, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("connections")
        .select(`
          *,
          requester:profiles!connections_requester_id_fkey(id, full_name, avatar_url, role),
          recipient:profiles!connections_recipient_id_fkey(id, full_name, avatar_url, role)
        `)
        .order("created_at", { ascending: false })
        .limit(100);

      if (activeTab !== "all") {
        query = query.eq("status", activeTab);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Filter by search query if provided
      if (searchQuery) {
        const lowerSearch = searchQuery.toLowerCase();
        return (data as ConnectionWithProfiles[]).filter(
          (c) =>
            c.requester?.full_name?.toLowerCase().includes(lowerSearch) ||
            c.recipient?.full_name?.toLowerCase().includes(lowerSearch)
        );
      }

      return data as ConnectionWithProfiles[];
    },
  });

  // Fetch connection statistics
  const { data: stats } = useQuery({
    queryKey: ["admin_connection_stats"],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [totalResult, pendingResult, acceptedTodayResult, requestsTodayResult] = await Promise.all([
        supabase.from("connections").select("id", { count: "exact", head: true }).eq("status", "accepted"),
        supabase.from("connections").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase
          .from("connections")
          .select("id", { count: "exact", head: true })
          .eq("status", "accepted")
          .gte("accepted_at", today.toISOString()),
        supabase
          .from("connections")
          .select("id", { count: "exact", head: true })
          .gte("created_at", today.toISOString()),
      ]);

      return {
        total_connections: totalResult.count || 0,
        pending_requests: pendingResult.count || 0,
        accepted_today: acceptedTodayResult.count || 0,
        requests_today: requestsTodayResult.count || 0,
      } as ConnectionStats;
    },
  });

  // Delete connection mutation
  const deleteConnection = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase.from("connections").delete().eq("id", connectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_connections"] });
      queryClient.invalidateQueries({ queryKey: ["admin_connection_stats"] });
      toast.success("Connection removed");
      setConnectionToDelete(null);
    },
    onError: (error: Error) => {
      toast.error("Failed to remove connection: " + error.message);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "accepted":
        return <Badge className="bg-green-100 text-green-700 border-green-200">Connected</Badge>;
      case "pending":
        return <Badge variant="outline" className="text-orange-600 border-orange-300">Pending</Badge>;
      case "declined":
        return <Badge variant="outline" className="text-red-600 border-red-300">Declined</Badge>;
      case "blocked":
        return <Badge variant="destructive">Blocked</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Community Connections</h2>
        <p className="text-sm text-muted-foreground">
          View and manage all connections between users
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Connections</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_connections ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Requests</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pending_requests ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted Today</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.accepted_today ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Requests Today</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.requests_today ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Connections List */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>All Connections</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="accepted">Connected</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="declined">Declined</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !connections?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  No connections found
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {connections.map((connection) => (
                      <div
                        key={connection.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card"
                      >
                        <div className="flex items-center gap-4">
                          {/* Requester */}
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarImage
                                src={getStorageUrl("avatars", connection.requester?.avatar_url) || undefined}
                              />
                              <AvatarFallback>
                                {connection.requester?.full_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">{connection.requester?.full_name}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {connection.requester?.role}
                              </p>
                            </div>
                          </div>

                          <span className="text-muted-foreground">→</span>

                          {/* Recipient */}
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              <AvatarImage
                                src={getStorageUrl("avatars", connection.recipient?.avatar_url) || undefined}
                              />
                              <AvatarFallback>
                                {connection.recipient?.full_name?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium">{connection.recipient?.full_name}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {connection.recipient?.role}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {getStatusBadge(connection.status)}
                          <span className="text-xs text-muted-foreground hidden sm:inline">
                            {formatDistanceToNow(new Date(connection.created_at), { addSuffix: true })}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => setConnectionToDelete(connection)}
                            aria-label={`Remove connection between ${connection.requester?.full_name} and ${connection.recipient?.full_name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!connectionToDelete} onOpenChange={() => setConnectionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the connection between{" "}
              <strong>{connectionToDelete?.requester?.full_name}</strong> and{" "}
              <strong>{connectionToDelete?.recipient?.full_name}</strong>?
              <br />
              <br />
              This action cannot be undone. Both users will lose access to each other's private
              profile information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => connectionToDelete && deleteConnection.mutate(connectionToDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteConnection.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Remove Connection"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
